/**
 * A NÉZŐ GEOMETRIÁJA — tiszta függvények, React és canvas nélkül.
 *
 * Három dolgot dönt el, és mindhármat KÜLÖN, mert mindháromnak más a
 * bukásmódja:
 *
 *   • a NAGYÍTÁS lépcsői — egy szabadon növő szorzó után a „100 %" soha
 *     nem jönne vissza pontosan;
 *   • az ILLESZTÉS — a lap a panel szélességére, forgatva a MAGASSÁGÁVAL;
 *   • a KOORDINÁTA-ÁTVÁLTÁS a képernyő és a lap között. Ez a #64 jelölőinek
 *     alapja: a jelölő helye a dokumentum SAJÁT álló helyzetében tárolódik
 *     (Viktor, 10-16), tehát elforgatott nézetben a kattintás helyét vissza
 *     kell forgatni — különben a jelölő a következő megnyitáskor máshol áll.
 */

/** Negyedfordulat, az óramutató járása szerint. */
export type Rotation = 0 | 90 | 180 | 270;

/** Egy lap mérete a dokumentum SAJÁT álló helyzetében, 1-es nagyításon. */
export interface PageSize {
  width: number;
  height: number;
}

/** Egy pont relatívan (0–1), a bal felső saroktól. */
export interface RelativePoint {
  x: number;
  y: number;
}

/**
 * A NAGYÍTÁS LÉPCSŐI. A „+" és a „−" ezeken lép; az illesztés ad közbülső
 * értéket, és onnan a következő lépcsőre ugrunk, nem szorzunk tovább.
 *
 * 10 %-IG LEMEGY (#68). Az alja korábban 50 % volt — egy nagy felbontású
 * telefonos fotó (4000 px széles) azon is a panel többszöröse, és a
 * könyvelő nem tudta egészében látni (Viktor, 10-19, éles használatban).
 */
export const ZOOM_STEPS = [0.1, 0.25, 0.33, 0.5, 0.67, 0.75, 1, 1.25, 1.5, 2, 3, 4] as const;

/** A lebegőpontos „majdnem egyenlő" — az illesztett érték a lépcső hajszálnyi közelébe eshet. */
const EPSILON = 1e-6;

/**
 * A LÉPÉS SOSEM MEGY A KÉRT IRÁNNYAL SZEMBEN. Az illesztés a lépcsőkön
 * kívülre is eshet: egy 12 MP-es telefonos fotó a keskeny hasábban ~6 %-on
 * fér el, és a „−" onnan a 10 %-os aljra ugorva NAGYÍTANA. Ilyenkor helyben
 * marad; fölfelé ugyanígy a 400 % fölötti illesztés.
 */
export function zoomIn(scale: number): number {
  return (
    ZOOM_STEPS.find((step) => step > scale + EPSILON) ??
    Math.max(scale, ZOOM_STEPS[ZOOM_STEPS.length - 1])
  );
}

export function zoomOut(scale: number): number {
  return (
    [...ZOOM_STEPS].reverse().find((step) => step < scale - EPSILON) ??
    Math.min(scale, ZOOM_STEPS[0])
  );
}

export function rotateClockwise(rotation: Rotation): Rotation {
  return ((rotation + 90) % 360) as Rotation;
}

export function rotateCounterClockwise(rotation: Rotation): Rotation {
  return ((rotation + 270) % 360) as Rotation;
}

function isSideways(rotation: Rotation): boolean {
  return rotation === 90 || rotation === 270;
}

/** A lap mérete a képernyőn — oldalra fordítva a szélesség és a magasság helyet cserél. */
export function displaySize(page: PageSize, scale: number, rotation: Rotation): PageSize {
  const width = page.width * scale;
  const height = page.height * scale;
  return isSideways(rotation) ? { width: height, height: width } : { width, height };
}

/**
 * A SZÉLESSÉGRE ILLESZTÉS — olvasásra (`W`). A könyvelő a fejlécet és a
 * végösszeget keresi, a keret görget. Az első nézet nem ez, hanem az egész
 * lap (`fitPageScale`, #68): előbb tájékozódik, aztán olvas.
 */
export function fitWidthScale(page: PageSize, rotation: Rotation, availableWidth: number): number {
  const width = isSideways(rotation) ? page.height : page.width;
  if (width <= 0 || availableWidth <= 0) {
    return 1;
  }
  return availableWidth / width;
}

/** A rendelkezésre álló hely a papír-dobozban. */
export interface AvailableSize {
  width: number;
  height: number;
}

/**
 * AZ EGÉSZ LAP ILLESZTÉSE — az első nézet (#68). A lap teljesen látszik:
 * a könyvelő előbb azt látja, HOL mi van, és onnan nagyít oda, ahová kell.
 * A szélességre illesztés (`fitWidthScale`) olvasásra való, ez tájékozódásra.
 *
 * Mérés nélkül (a panel még nincs kirajzolva) 1, ahogy a szélességnél.
 */
export function fitPageScale(page: PageSize, rotation: Rotation, available: AvailableSize): number {
  const shown = displaySize(page, 1, rotation);
  if (shown.width <= 0 || shown.height <= 0 || available.width <= 0 || available.height <= 0) {
    return 1;
  }
  return Math.min(available.width / shown.width, available.height / shown.height);
}

/**
 * A KÉPERNYŐ PONTJÁBÓL A LAP PONTJA — a dokumentum saját álló helyzetében.
 *
 * A bemenet a megjelenített (elforgatott) lap dobozán belüli relatív pont.
 * A forgatás az óramutató járása szerint értendő: 90°-nál a lap bal felső
 * sarka a jobb felsőbe kerül.
 */
export function toPagePoint(display: RelativePoint, rotation: Rotation): RelativePoint {
  const { x, y } = display;
  switch (rotation) {
    case 0:
      return { x, y };
    case 90:
      return { x: y, y: 1 - x };
    case 180:
      return { x: 1 - x, y: 1 - y };
    case 270:
      return { x: 1 - y, y: x };
  }
}

/** A LAP PONTJÁBÓL A KÉPERNYŐ PONTJA — a `toPagePoint` megfordítása. */
export function fromPagePoint(page: RelativePoint, rotation: Rotation): RelativePoint {
  const { x, y } = page;
  switch (rotation) {
    case 0:
      return { x, y };
    case 90:
      return { x: 1 - y, y: x };
    case 180:
      return { x: 1 - x, y: 1 - y };
    case 270:
      return { x: y, y: 1 - x };
  }
}

/**
 * A TÁROLT HELY 6 TIZEDESRE kerekítve. Ez egy 300 dpi-s A4-es lapon is a
 * pixel töredéke — és a rövid tizedes tört a szerveren át BETŰRE ugyanaz jön
 * vissza, nem egy utolsó jegyében eltérő `double`.
 */
export function roundPoint(point: RelativePoint): RelativePoint {
  return { x: Math.round(point.x * 1e6) / 1e6, y: Math.round(point.y * 1e6) / 1e6 };
}

/** A lapra szorítva: a jelölő a lap szélén még a lapon van, azon túl már nem. */
export function clampToPage(point: RelativePoint): RelativePoint {
  return { x: clampUnit(point.x), y: clampUnit(point.y) };
}

function clampUnit(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * A LETÉTEL ALATT ÁLLÓ JELÖLŐ LÉPÉSE a nyilakkal — a KÉPERNYŐ irányában,
 * nem a lapéban: elforgatott nézetben a „fel" az, ami a könyvelőnek fent van.
 * A lap szélén megáll, nem fordul át.
 */
export function nudge(point: RelativePoint, dx: number, dy: number): RelativePoint {
  return clampToPage({ x: point.x + dx, y: point.y + dy });
}

/** A nagyítás kiírt alakja: „125 %". A magyar helyesírás szerint szóközzel. */
export function zoomLabel(scale: number): string {
  return `${Math.round(scale * 100)} %`;
}
